// TelegramClient covers the inbound-control surface for a sandbox's Telegram
// bot (Feature 2). The bot is a project-scoped TELEGRAM credential attached to
// the sandbox; these endpoints enable the inbound webhook, mint single-use link
// codes (pasted in Telegram as `/link <code>`), and list/revoke chat bindings.
//
// The link code is returned EXACTLY ONCE at mint time — surface it immediately.
// The backend stores only its hash.
import type { BaseClient } from '../../http/BaseClient.js';

export interface TelegramBinding {
  id: string;
  telegramChatId: string;
  telegramUserId?: string | null;
  label?: string | null;
  status: string;
  createdAt: string;
  lastUsedAt?: string | null;
}

export interface MintLinkCodeResponse {
  code: string;
  expiresInSeconds: number;
}

/** A chat the bot has seen, surfaced for chat-ID auto-fill during enrolment. */
export interface TelegramDiscoveredChat {
  id: string;
  /** private | group | supergroup | channel */
  type: string;
  /** friendly display name (group/channel title, or the user's name) */
  title: string;
}

/**
 * A chat already linked to a bot credential (via `/link`). Surfaced as a
 * pick-list for the default outbound Chat ID when inbound is enabled and live
 * getUpdates detection is therefore unavailable.
 */
export interface TelegramKnownChat {
  id: string;
  /** friendly name captured at link time; absent for older bindings */
  label?: string | null;
}

/** The bot's identity + state, used to build the "open your bot" deep link. */
export interface TelegramBotInfo {
  /** bot @username (without the @) — link is https://t.me/<username> */
  username: string;
  name: string;
  /** true when a webhook is set (inbound enabled) — chat detection won't work */
  webhookSet: boolean;
}

export class TelegramClient {
  constructor(private readonly http: BaseClient) {}

  /**
   * Look up a bot's identity (and whether inbound/webhook is active) from its
   * token, so the UI can show a one-click "open @bot in Telegram" link. The
   * token is used server-side transiently and not stored.
   */
  async botInfo(token: string): Promise<TelegramBotInfo> {
    const res = await this.http.post<{ success: boolean; bot?: { username: string; name: string; webhook_set: boolean } }>(
      '/credentials/telegram/bot-info',
      { body: { token } },
    );
    const b = res.bot;
    if (!b) throw new Error('botInfo response missing bot.');
    return { username: b.username, name: b.name, webhookSet: b.webhook_set };
  }

  /**
   * Discover the chats a bot can post to, for chat-ID auto-fill during
   * enrolment. The user creates the bot, sends it a message (or adds it to a
   * group/channel), then this returns the chats it has seen — no manual
   * getUpdates call. The token is used server-side transiently and not stored.
   * Returns [] when the bot hasn't been messaged yet.
   */
  async discoverChats(token: string): Promise<TelegramDiscoveredChat[]> {
    const res = await this.http.post<{ success: boolean; chats?: TelegramDiscoveredChat[] }>(
      '/credentials/telegram/discover-chats',
      { body: { token } },
    );
    return res.chats ?? [];
  }

  /**
   * List the chats already linked to a bot credential (from `/link`
   * redemptions). Unlike discoverChats (which calls getUpdates and fails while
   * a webhook is active), this reads our stored bindings, so it works precisely
   * when inbound is enabled. Returns [] when nothing has linked yet.
   */
  async knownChats(credentialId: string): Promise<TelegramKnownChat[]> {
    const res = await this.http.get<{ success: boolean; chats?: TelegramKnownChat[] }>(
      `/credentials/telegram/${credentialId}/chats`,
    );
    return res.chats ?? [];
  }

  /**
   * Remove the bot's webhook on Telegram (deleteWebhook) so getUpdates chat
   * detection works again. Used to reclaim a bot that still has a webhook from a
   * previous setup (an orphan whose owning credential is gone). Note this frees
   * the bot globally — any inbound on it stops until re-enabled. The token is
   * used server-side transiently and not stored.
   */
  async clearWebhook(token: string): Promise<void> {
    await this.http.post<{ success: boolean }>('/credentials/telegram/clear-webhook', { body: { token } });
  }

  /** Enable inbound: register the bot's webhook (anchored at this sandbox). */
  enableInbound(sandboxId: string, credentialId: string): Promise<{ success: boolean }> {
    return this.http.post<{ success: boolean }>(`/sandbox/${sandboxId}/telegram/enable`, {
      body: { credential_id: credentialId },
    });
  }

  /** Disable inbound for the bot (bindings are kept; re-enable rekeys). */
  disableInbound(sandboxId: string, credentialId: string): Promise<{ success: boolean }> {
    return this.http.post<{ success: boolean }>(`/sandbox/${sandboxId}/telegram/disable`, {
      body: { credential_id: credentialId },
    });
  }

  /**
   * Mint a single-use link code. The plaintext `code` is returned once — show
   * it to the user to paste in Telegram as `/link <code>`.
   */
  async mintLinkCode(sandboxId: string, credentialId: string): Promise<MintLinkCodeResponse> {
    const res = await this.http.post<{ success: boolean; code?: string; expires_in_seconds?: number }>(
      `/sandbox/${sandboxId}/telegram/link-code`,
      { body: { credential_id: credentialId } },
    );
    if (!res.code) {
      throw new Error('mintLinkCode response missing code.');
    }
    return { code: res.code, expiresInSeconds: res.expires_in_seconds ?? 0 };
  }

  /** List active chat bindings for this sandbox. */
  async listBindings(sandboxId: string): Promise<TelegramBinding[]> {
    const res = await this.http.get<{ success: boolean; bindings?: TelegramBinding[] }>(
      `/sandbox/${sandboxId}/telegram/bindings`,
    );
    return res.bindings ?? [];
  }

  /** Revoke a chat binding — the chat stops driving the agent immediately. */
  revokeBinding(sandboxId: string, bindingId: string): Promise<{ success: boolean }> {
    return this.http.delete<{ success: boolean }>(`/sandbox/${sandboxId}/telegram/bindings/${bindingId}`);
  }
}
